import React, { useEffect, useState, useCallback } from 'react'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'

import TextField from '../../../../../components/FormLegacy/FormFieldsV2/TextField'
import ModalContainer from '../../../../../components/ModalContainer/ModelContainer'
import Icon from '../../../../../components/Icons'
import MediaUpload from '../../../../../components/MediaUpload/MediaUpload'
import TextAreaField from '../../../../../components/FormLegacy/FormFieldsV2/TextAreaField'
import PhoneField from '../../../../../components/FormLegacy/FormFieldsV2/PhoneField'
import ProviderItem from '../components/ProviderItem'
import { getProvidersTypeFilter } from '../../helpers'
import { useSnackbar } from '../../../../../helpers/hooks/useSnackbar'
import api from '../../../../../api/api'

const MAX_TEXT_LENGTH = 30
const MAX_DESCRIPTION_LENGTH = 255

const AddProviderModal = ({
  onClose = () => {},
  onAddProvider = () => {},
  onEditProvider = () => {},
  provider,
}) => {
  const { t } = useTranslation(['checklists-create-task', 'common'])
  const providersTypeFilter = getProvidersTypeFilter(t)
  const [formState, setFormState] = useState({
    name: provider ? provider.name : '',
    logo: provider ? provider.logo : '',
    phoneNumber: provider ? provider.phoneNumber : '',
    siteUrl: provider ? provider.siteUrl : '',
    description: provider ? provider.description : '',
  })
  const [isLoading, setIsLoading] = useState(false)
  const [addNew, setAddNew] = useState(false)
  const [selectedTab, setSelectedTab] = useState(providersTypeFilter[0])
  const [providers, setProviders] = useState([])
  const [data, setData] = useState([])
  const [search, setSearch] = useState('')
  const [prevSearchValue, setPrevSearchValue] = useState('')

  const { displaySnackbar } = useSnackbar()
  const addOrUpdate = addNew || provider

  const handleFieldChange = ({ target: { name, value } }) => {
    setFormState((prev) => ({ ...prev, [name]: value }))
  }

  const filterProviders = useCallback(() => {
    const filtered = providers.filter(
      (provider) =>
        provider.name.toLowerCase().includes(search.toLowerCase()) &&
        (selectedTab.value === 'all' || selectedTab.value === provider.type),
    )

    setData(filtered)
  }, [providers, search, selectedTab])

  const fetchProviders = async () => {
    setIsLoading(true)
    try {
      const { data } = await api.getUtilityProviders()
      setProviders(data)
    } catch ({ message }) {
      displaySnackbar(message, true)
    } finally {
      setIsLoading(false)
    }
  }

  const onFileUpload = (file) => {
    if (file) {
      handleFieldChange({ target: { name: 'logo', value: file } })
    }
  }

  const handleTextChange = (e) => {
    if (e.target.value.length < MAX_TEXT_LENGTH) {
      handleFieldChange(e)
    }
  }

  const handleDescriptionChange = (e) => {
    if (e.target.value.length < MAX_DESCRIPTION_LENGTH) {
      handleFieldChange(e)
    }
  }

  const onSelectedProvider = (provider) => {
    onAddProvider(provider)
    onClose()
  }

  const onEditedProvider = (provider) => {
    onEditProvider(provider)
    onClose()
  }

  const createProvider = async (provider) => {
    setIsLoading(true)
    try {
      const { data } = await api.createUtilityProvider(provider)
      onSelectedProvider(data)
    } catch ({ message }) {
      displaySnackbar(message, true)
    } finally {
      setIsLoading(false)
    }
  }

  const updateProvider = async (provider) => {
    setIsLoading(true)
    try {
      const { data } = await api.updateUtilityProvider(provider.id, provider)
      onEditedProvider(data)
    } catch ({ message }) {
      displaySnackbar(message, true)
    } finally {
      setIsLoading(false)
    }
  }

  const canSaveChanges =
    provider &&
    ((formState.name && formState.name !== provider.name) ||
      formState.logo !== provider.logo ||
      (formState.phoneNumber && formState.phoneNumber !== provider.phoneNumber) ||
      (formState.siteUrl && formState.siteUrl !== provider.siteUrl) ||
      formState.description !== provider.description)

  const canCreateProvider = formState.name && formState.phoneNumber && formState.siteUrl

  useEffect(() => {
    if (prevSearchValue !== search) {
      window.addEventListener('keydown', filterProviders())
    }
    setPrevSearchValue(search)
    return () => {
      window.removeEventListener('keydown', filterProviders)
    }
  }, [search])

  useEffect(() => {
    if (!addOrUpdate) {
      fetchProviders()
    }
  }, [addOrUpdate])

  useEffect(() => {
    filterProviders()
  }, [selectedTab, providers])

  const view = addOrUpdate ? (
    <React.Fragment>
      <div className="checklist-modal-content checklist-add-provider">
        <div className="checklist-modal-input-row">
          <div className="input-container">
            <div className="input-label">
              {t('checklists-create-task:addProviderModal.providerNameLabel')}
            </div>
            <TextField
              name="name"
              onChange={handleTextChange}
              value={formState.name}
              placeholder={t('checklists-create-task:addProviderModal.providerNamePlaceholder')}
              style={{ width: '100%' }}
            />
            <span className="input-desc-label">
              {t('checklists-create-task:dataInput.maxLengthInfo', { length: MAX_TEXT_LENGTH })}
            </span>
          </div>
        </div>
        <div className="checklist-modal-input-row">
          <div className="input-container">
            <div className="input-label">
              {t('checklists-create-task:addProviderModal.uploadLogoLabel')}{' '}
              <small style={{ fontSize: 12 }}>
                ({t('checklists-create-task:shared.optionalSuffix')})
              </small>
            </div>
            <MediaUpload files={[formState.logo]} onUpdate={onFileUpload} mediaTypes={['image']} />
            <span className="input-desc-label">
              {t('checklists-create-task:addProviderModal.logoRecommendationInfo')}
            </span>
          </div>
        </div>
        <div className="checklist-modal-input-row">
          <div className="input-container">
            <div className="input-label">
              {t('checklists-create-task:addProviderModal.phoneNumberLabel')}
            </div>
            <PhoneField
              type={'number'}
              data-testid="phoneNumber"
              name="phoneNumber"
              placeholder={t('checklists-create-task:addProviderModal.phoneNumberPlaceholder')}
              value={formState.phoneNumber}
              onChange={handleFieldChange}
            />
          </div>
        </div>
        <div className="checklist-modal-input-row">
          <div className="input-container">
            <div className="input-label">
              {t('checklists-create-task:addProviderModal.websiteUrlLabel')}
            </div>
            <TextField
              name="siteUrl"
              onChange={handleTextChange}
              value={formState.siteUrl}
              placeholder={t('checklists-create-task:addProviderModal.websiteUrlPlaceholder')}
              style={{ width: '100%' }}
            />
          </div>
        </div>
        <div className="checklist-modal-input-row">
          <div className="input-container">
            <div className="input-label">
              {t('checklists-create-task:addProviderModal.descriptionLabel')}{' '}
              <small style={{ fontSize: 12 }}>
                ({t('checklists-create-task:shared.optionalSuffix')})
              </small>
            </div>
            <TextAreaField
              name="description"
              onChange={handleDescriptionChange}
              value={formState.description}
              placeholder={t('checklists-create-task:addProviderModal.descriptionPlaceholder')}
              style={{ width: '100%' }}
            />
            <span className="input-desc-label">
              {t('checklists-create-task:addProviderModal.descriptionMaxLengthInfo', {
                length: MAX_DESCRIPTION_LENGTH,
              })}
            </span>
          </div>
        </div>
      </div>
      <div className="checklist-modal-actions">
        <button
          className="button button-primary"
          onClick={() =>
            provider ? updateProvider({ ...provider, ...formState }) : createProvider(formState)
          }
          disabled={provider ? !canSaveChanges : !canCreateProvider}
        >
          {provider
            ? t('checklists-create-task:addProviderModal.saveChangesButton')
            : t('checklists-create-task:addProviderModal.addProviderButton')}
        </button>
      </div>
    </React.Fragment>
  ) : (
    <React.Fragment>
      <div
        className={`checklist-modal-content checklist-add-provider ${
          isLoading ? 'is-loading' : ''
        }`}
      >
        <div className="checklist-add-provider-head">
          <span>{t('checklists-create-task:addProviderModal.selectPreferredProviders')}</span>
          <div>
            <span>{t('checklists-create-task:addProviderModal.dontSeeProviderInfo')}</span>
            <button className="button button-black" onClick={() => setAddNew(true)}>
              <Icon icon="plus" viewBox="0 -2 15 15" />
              {t('checklists-create-task:addProviderModal.addNewProviderButton')}
            </button>
          </div>
        </div>
        <TextField
          placeholder={t('checklists-create-task:addProviderModal.searchByProviderNamePlaceholder')}
          style={{ width: '100%' }}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          leftIcon={<Icon icon="search" />}
        />
        <div className="checklist-add-provider-filters">
          {providersTypeFilter.map((tabFilter) => (
            <div
              className={`provider-filter ${
                selectedTab && tabFilter.id === selectedTab.id ? 'active' : ''
              }`}
              key={tabFilter.id}
              onClick={() => setSelectedTab(tabFilter)}
            >
              {tabFilter.text}
            </div>
          ))}
        </div>
        <div className="checklist-add-provider-providers">
          {data.map((provider) => (
            <ProviderItem
              key={provider.id}
              provider={provider}
              onClick={() => createProvider(provider)}
            />
          ))}
        </div>
      </div>
      <div className="checklist-modal-actions">
        <button className="button button-gray" onClick={onClose}>
          {t('common:actions.back')}
        </button>
      </div>
    </React.Fragment>
  )

  return (
    <ModalContainer
      additionalClass={`checklist-modal`}
      onClose={onClose}
      headerTitle={
        provider
          ? t('checklists-create-task:addProviderModal.updateProviderHeaderTitle')
          : t('checklists-create-task:addProviderModal.addProviderHeaderTitle')
      }
    >
      {view}
    </ModalContainer>
  )
}

AddProviderModal.propTypes = {
  onClose: PropTypes.func,
  onAddProvider: PropTypes.func,
  onEditProvider: PropTypes.func,
  provider: PropTypes.object,
}

export default AddProviderModal
